import React, { useMemo, useRef, useState } from "react";
import { GANTT_STATUS_COLORS, flattenTree } from "@/lib/taskman";

const DAY_W = 26;
const ROW_H = 36;
const LABEL_W = 260;

const toDate = (s) => (s ? new Date(`${s}T00:00:00`) : null);
const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const diffDays = (a, b) => Math.round((b - a) / 86400000);

/**
 * Interactive Gantt: drag a bar to move it, drag its edges to resize.
 * Dependency arrows are drawn between consecutive siblings whose parent
 * container runs in 'sequential' (priority-order) mode.
 */
export default function GanttChart({ tasks, rootSequential = false, onUpdateDates }) {
  const rows = useMemo(() => flattenTree(tasks), [tasks]);
  const [drag, setDrag] = useState(null); // {taskId, mode, startX, s, d, delta}
  const scrollRef = useRef(null);

  const { min, days } = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let lo = null, hi = null;
    rows.forEach((t) => {
      const s = toDate(t.start_date), e = toDate(t.due_date);
      if (s && (!lo || s < lo)) lo = s;
      if (e && (!hi || e > hi)) hi = e;
    });
    if (!lo) lo = today;
    if (!hi) hi = addDays(today, 21);
    lo = addDays(lo, -4); hi = addDays(hi, 8);
    return { min: lo, days: diffDays(lo, hi) + 1 };
  }, [rows]);

  const chartW = days * DAY_W;

  const barGeom = (t) => {
    let s = toDate(t.start_date), e = toDate(t.due_date);
    if (!s && !e) return null;
    s = s || e; e = e || s;
    let sIdx = diffDays(min, s), len = diffDays(s, e) + 1;
    if (drag && drag.taskId === t.task_id) {
      if (drag.mode === "move") sIdx += drag.delta;
      else if (drag.mode === "resize-l") { sIdx += drag.delta; len -= drag.delta; }
      else if (drag.mode === "resize-r") len += drag.delta;
      if (len < 1) len = 1;
    }
    return { x: sIdx * DAY_W, w: len * DAY_W, sIdx, len };
  };

  const startDrag = (e, task, mode) => {
    e.preventDefault(); e.stopPropagation();
    setDrag({ taskId: task.task_id, mode, startX: e.clientX, delta: 0 });
    const onMove = (ev) => {
      setDrag((d) => d && { ...d, delta: Math.round((ev.clientX - d.startX) / DAY_W) });
    };
    const onUp = (ev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      const delta = Math.round((ev.clientX - e.clientX) / DAY_W);
      setDrag(null);
      if (!delta) return;
      let s = toDate(task.start_date) || toDate(task.due_date);
      let d2 = toDate(task.due_date) || s;
      if (mode === "move") { s = addDays(s, delta); d2 = addDays(d2, delta); }
      else if (mode === "resize-l") { s = addDays(s, delta); if (s > d2) s = d2; }
      else { d2 = addDays(d2, delta); if (d2 < s) d2 = s; }
      onUpdateDates(task.task_id, { start_date: iso(s), due_date: iso(d2) });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // dependency arrows between sequential siblings
  const deps = useMemo(() => {
    const groups = new Map();
    rows.forEach((t, i) => {
      const key = t.parent_task_id || "__root__";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ t, rowIdx: i });
    });
    const byId = new Map(rows.map((t) => [t.task_id, t]));
    const pairs = [];
    groups.forEach((arr, key) => {
      const seq = key === "__root__"
        ? rootSequential
        : byId.get(key)?.execution_mode === "sequential";
      if (!seq) return;
      arr.sort((a, b) => (a.t.order_index ?? 0) - (b.t.order_index ?? 0));
      for (let i = 0; i < arr.length - 1; i++) pairs.push([arr[i], arr[i + 1]]);
    });
    return pairs;
  }, [rows, rootSequential]);

  const monthSpans = useMemo(() => {
    const spans = [];
    for (let i = 0; i < days; i++) {
      const d = addDays(min, i);
      const label = d.toLocaleDateString("en-AU", { month: "short", year: "numeric" });
      if (spans.length && spans[spans.length - 1].label === label) spans[spans.length - 1].n++;
      else spans.push({ label, n: 1 });
    }
    return spans;
  }, [min, days]);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayIdx = diffDays(min, today);

  if (!rows.length) {
    return <div className="text-sm text-zinc-400 py-10 text-center" data-testid="gantt-empty">No tasks to chart yet.</div>;
  }

  return (
    <div className="border border-zinc-200 rounded-sm overflow-hidden" data-testid="gantt-chart">
      <div className="flex">
        {/* Fixed label column */}
        <div className="shrink-0 border-r border-zinc-200 bg-white" style={{ width: LABEL_W }}>
          <div className="h-[44px] border-b border-zinc-200 px-3 flex items-end pb-1 text-[10px] uppercase tracking-widest text-zinc-400">Task</div>
          {rows.map((t) => (
            <div key={t.task_id} className="flex items-center gap-1 px-3 border-b border-zinc-100 text-xs text-zinc-700"
              style={{ height: ROW_H, paddingLeft: 12 + t.depth * 14 }}>
              <span className="truncate">{t.title}</span>
            </div>
          ))}
        </div>
        {/* Scrollable timeline */}
        <div className="overflow-x-auto flex-1" ref={scrollRef}>
          <div style={{ width: chartW, position: "relative" }}>
            {/* header */}
            <div className="h-[44px] border-b border-zinc-200 bg-zinc-50/60">
              <div className="flex h-[22px]">
                {monthSpans.map((m, i) => (
                  <div key={i} style={{ width: m.n * DAY_W }}
                    className="text-[10px] font-semibold text-zinc-500 px-1 border-r border-zinc-200 truncate leading-[22px]">
                    {m.label}
                  </div>
                ))}
              </div>
              <div className="flex h-[22px]">
                {Array.from({ length: days }).map((_, i) => {
                  const d = addDays(min, i);
                  const we = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <div key={i} style={{ width: DAY_W }}
                      className={`text-[9px] text-center leading-[22px] border-r border-zinc-100 ${we ? "bg-zinc-100 text-zinc-400" : "text-zinc-500"}`}>
                      {d.getDate()}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* grid + bars */}
            <div style={{ position: "relative" }}>
              {/* weekend shading */}
              {Array.from({ length: days }).map((_, i) => {
                const d = addDays(min, i);
                const we = d.getDay() === 0 || d.getDay() === 6;
                return we ? (
                  <div key={i} className="absolute top-0 bottom-0 bg-zinc-50"
                    style={{ left: i * DAY_W, width: DAY_W }} />
                ) : null;
              })}
              {/* today line */}
              {todayIdx >= 0 && todayIdx < days && (
                <div className="absolute top-0 bottom-0 w-[2px] bg-red-400/70 z-10"
                  style={{ left: todayIdx * DAY_W + DAY_W / 2 }} data-testid="gantt-today-line" />
              )}
              {rows.map((t, ri) => {
                const g = barGeom(t);
                return (
                  <div key={t.task_id} className="relative border-b border-zinc-100"
                    style={{ height: ROW_H }}>
                    {g ? (
                      <div
                        className="absolute rounded-sm shadow-sm cursor-grab active:cursor-grabbing select-none flex items-center"
                        style={{
                          left: g.x, width: g.w, top: 7, height: ROW_H - 14,
                          background: GANTT_STATUS_COLORS[t.status] || "#a1a1aa",
                          opacity: drag?.taskId === t.task_id ? 0.75 : 0.95,
                        }}
                        onPointerDown={(e) => startDrag(e, t, "move")}
                        title={`${t.title} · ${t.start_date || "?"} → ${t.due_date || "?"} (drag to move)`}
                        data-testid={`gantt-bar-${t.task_id}`}
                      >
                        <div className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize rounded-l-sm hover:bg-black/20"
                          onPointerDown={(e) => startDrag(e, t, "resize-l")} />
                        <span className="text-[10px] text-white font-medium px-2 truncate pointer-events-none">
                          {g.w > 60 ? t.title : ""}
                        </span>
                        <div className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize rounded-r-sm hover:bg-black/20"
                          onPointerDown={(e) => startDrag(e, t, "resize-r")} />
                      </div>
                    ) : (
                      <button type="button"
                        className="absolute top-2 text-[10px] text-zinc-400 border border-dashed border-zinc-300 rounded-sm px-2 py-0.5 hover:text-zinc-700 hover:border-zinc-400"
                        style={{ left: Math.max(todayIdx, 0) * DAY_W }}
                        onClick={() => onUpdateDates(t.task_id, {
                          start_date: iso(today), due_date: iso(addDays(today, 2)),
                        })}
                        data-testid={`gantt-schedule-${t.task_id}`}>
                        + Schedule
                      </button>
                    )}
                  </div>
                );
              })}
              {/* dependency arrows */}
              <svg className="absolute inset-0 pointer-events-none z-20"
                width={chartW} height={rows.length * ROW_H}>
                <defs>
                  <marker id="gantt-arrow" markerWidth="6" markerHeight="6"
                    refX="5" refY="3" orient="auto">
                    <path d="M0,0 L6,3 L0,6 z" fill="#71717a" />
                  </marker>
                </defs>
                {deps.map(([a, b], i) => {
                  const ga = barGeom(a.t), gb = barGeom(b.t);
                  if (!ga || !gb) return null;
                  const x1 = ga.x + ga.w, y1 = a.rowIdx * ROW_H + ROW_H / 2;
                  const x2 = gb.x, y2 = b.rowIdx * ROW_H + ROW_H / 2;
                  const midX = x1 + Math.max(10, (x2 - x1) / 2);
                  return (
                    <path key={i}
                      d={`M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2 - 3} ${y2}`}
                      fill="none" stroke="#71717a" strokeWidth="1.5"
                      strokeDasharray="3 3" markerEnd="url(#gantt-arrow)" />
                  );
                })}
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
